import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import SignaturePadField from "../components/SignaturePadField";
import {
  ArrowLeft,
  Check,
  Loader2,
  AlertCircle,
  PenLine,
  User,
  Users,
  Euro,
  FileText,
  MapPin,
  Calendar,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { isDepositoCauzionaleAbilitato, importoDepositoCauzionale } from "../utils/depositoCauzionale";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/** Formatta una data ISO (YYYY-MM-DD) o già localizzata in formato italiano dd/mm/yyyy. */
function formatDataIt(raw) {
  if (!raw) return "N/D";
  // Se è già nel formato dd/mm/yyyy non toccare
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
  // ISO YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const [y, m, d] = raw.split("T")[0].split("-");
    return `${d}/${m}/${y}`;
  }
  // Fallback: prova con Date
  try {
    return new Date(raw).toLocaleDateString("it-IT");
  } catch {
    return raw;
  }
}

/** Ricava il nome completo dell'ospite dal fascicolo, con fallback robusto. */
function getNomeOspite(fascicolo) {
  if (!fascicolo) return "";
  if (fascicolo.nome_completo) return fascicolo.nome_completo;
  const ana = fascicolo.anagrafica || {};
  const nome = [ana.nome, ana.cognome].filter(Boolean).join(" ").trim();
  return nome || "";
}

export default function FirmaContratto() {
  const { id: fascicoloId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fascicolo, setFascicolo] = useState(null);
  const [contractStatus, setContractStatus] = useState(null);
  const [templateData, setTemplateData] = useState(null);
  const [luogo, setLuogo] = useState("");
  const [testoOpen, setTestoOpen] = useState(false);

  const sigPadRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, [fascicoloId, token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [fascicoloRes, statusRes, templateRes, strutture] = await Promise.all([
        axios.get(`${API_URL}/api/fascicoli-assistenziali/${fascicoloId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/contract/check/${fascicoloId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/contract-template`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/api/strutture`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setFascicolo(fascicoloRes.data);
      setContractStatus(statusRes.data);

      const tmpl = templateRes.data.template;
      setTemplateData(tmpl);

      // Pre-compila il luogo con il comune della struttura (primaria o prima)
      const strutturaList = strutture.data?.strutture || [];
      const primary = strutturaList.find((s) => s.is_primary) || strutturaList[0];
      if (primary?.comune) setLuogo(primary.comune);

    } catch (error) {
      console.error("Errore caricamento dati:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const handleSign = async () => {
    if (!luogo.trim()) {
      toast.error("Inserisci il luogo della firma");
      return;
    }
    if (sigPadRef.current?.isEmpty()) {
      toast.error("Disegna la firma prima di procedere");
      return;
    }
    setSaving(true);
    try {
      const firmaFamiliare = sigPadRef.current.toDataURL("image/png");
      await axios.post(
        `${API_URL}/api/contract/sign`,
        { fascicolo_id: fascicoloId, firma_familiare: firmaFamiliare, luogo: luogo.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Contratto firmato con successo!");
      navigate(`/fascicoli-assistenziali/${fascicoloId}`);
    } catch (error) {
      console.error("Errore firma:", error);
      toast.error(error.response?.data?.detail || "Errore nella firma del contratto");
    } finally {
      setSaving(false);
    }
  };

  /* ─── Loading ─── */
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-indigo-400" />
      </div>
    );
  }

  /* ─── Template non configurato ─── */
  if (!contractStatus?.hasTemplate) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
          <AlertCircle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Template non configurato</h2>
          <p className="text-slate-500 mb-6 text-sm">
            Configura logo e testo del contratto nelle Impostazioni della struttura.
          </p>
          <Button onClick={() => navigate("/impostazioni")}>Vai alle Impostazioni</Button>
        </div>
      </div>
    );
  }

  /* ─── Già firmato ─── */
  if (contractStatus?.hasSigned) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl p-10 max-w-md w-full text-center shadow-2xl">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-emerald-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Contratto già firmato</h2>
          <p className="text-slate-500 mb-6 text-sm">
            Firmato il {new Date(contractStatus.signedAt).toLocaleDateString("it-IT")}
          </p>
          <Button variant="outline" onClick={() => navigate(`/fascicoli-assistenziali/${fascicoloId}`)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna al Fascicolo
          </Button>
        </div>
      </div>
    );
  }

  const anagrafica = fascicolo?.anagrafica || {};
  const amministrativa = fascicolo?.amministrativa || {};
  const contatti = anagrafica.contatti_familiari || [];
  const familiare = contatti[0] || {};
  const nomeOspite = getNomeOspite(fascicolo);
  const oggi = new Date().toLocaleDateString("it-IT");

  // Campi obbligatori mancanti — blocca la firma se ce ne sono
  const missingFields = [];
  if (!nomeOspite) missingFields.push({ section: "Dati Ospite", field: "Nome e Cognome" });
  if (!anagrafica.codice_fiscale) missingFields.push({ section: "Dati Ospite", field: "Codice Fiscale" });
  if (!anagrafica.data_nascita) missingFields.push({ section: "Dati Ospite", field: "Data di Nascita" });
  if (!familiare.nome) missingFields.push({ section: "Familiare / Tutore", field: "Nome e Cognome" });
  if (!familiare.relazione) missingFields.push({ section: "Familiare / Tutore", field: "Relazione" });
  if (!familiare.telefono) missingFields.push({ section: "Familiare / Tutore", field: "Telefono" });
  if (amministrativa.retta_mensile == null || amministrativa.retta_mensile === "" || Number(amministrativa.retta_mensile) === 0)
    missingFields.push({ section: "Condizioni Economiche", field: "Retta Mensile" });
  if (isDepositoCauzionaleAbilitato(amministrativa)) {
    const dep = importoDepositoCauzionale(amministrativa);
    if (dep == null)
      missingFields.push({ section: "Condizioni Economiche", field: "Deposito cauzionale" });
  }
  const canSign = missingFields.length === 0;

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">

      {/* ── Top bar ── */}
      <div className="flex items-center justify-between px-4 py-3 bg-slate-800 border-b border-slate-700">
        <button
          onClick={() => navigate(`/fascicoli-assistenziali/${fascicoloId}`)}
          className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Esci
        </button>
        <span className="text-white font-semibold text-sm tracking-wide">Firma Contratto</span>
        <span className="text-slate-400 text-sm truncate max-w-[160px]">{nomeOspite}</span>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl px-4 py-5 space-y-4">

          {/* ── Riepilogo sempre visibile ── */}
          <div className="border border-slate-700 rounded-xl bg-slate-800/60 backdrop-blur overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-700">
              <FileText className="w-4 h-4 text-indigo-400" />
              <span className="text-sm font-medium text-white">Riepilogo contratto</span>
            </div>

            <div className="px-4 pt-3 pb-4 space-y-4 text-sm">
              {/* Dati ospite + familiare */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <p className="text-xs text-indigo-400 font-semibold uppercase tracking-wide flex items-center gap-1">
                    <User className="w-3 h-3" /> Ospite
                  </p>
                  <SummaryRow label="Nome" value={nomeOspite} />
                  <SummaryRow label="C.F." value={anagrafica.codice_fiscale} />
                  <SummaryRow label="Nato il" value={formatDataIt(anagrafica.data_nascita)} />
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-indigo-400 font-semibold uppercase tracking-wide flex items-center gap-1">
                    <Users className="w-3 h-3" /> Familiare / Tutore
                  </p>
                  <SummaryRow label="Nome" value={familiare.nome} />
                  <SummaryRow label="Relazione" value={familiare.relazione} />
                  <SummaryRow label="Tel." value={familiare.telefono} />
                </div>
              </div>

              {/* Condizioni economiche */}
              <div className="flex items-center gap-4 bg-emerald-900/30 border border-emerald-800/40 rounded-lg px-3 py-2">
                <Euro className="w-4 h-4 text-emerald-400 shrink-0" />
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
                  <span className="text-slate-300">
                    Retta: <span className="text-emerald-400 font-bold">€ {amministrativa.retta_mensile || "N/D"}</span>
                  </span>
                  <span className="text-slate-300">
                    Ente: <span className="text-white font-medium">{amministrativa.ente_pagante || "Privato"}</span>
                  </span>
                  <span className="text-slate-300">
                    Contratto: <span className="text-white font-medium">{amministrativa.tipo_contratto || "Tempo Indeterminato"}</span>
                  </span>
                  {isDepositoCauzionaleAbilitato(amministrativa) && (
                    <span className="text-slate-300">
                      Deposito:{" "}
                      <span className="text-white font-medium">
                        {importoDepositoCauzionale(amministrativa) != null
                          ? `€ ${importoDepositoCauzionale(amministrativa).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`
                          : "mancante"}
                      </span>
                    </span>
                  )}
                </div>
              </div>

              {/* Testo contratto — collassabile */}
              {templateData?.body_text && (
                <div className="border border-slate-700 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setTestoOpen((v) => !v)}
                    className="w-full flex items-center justify-between px-3 py-2 bg-slate-700/50 hover:bg-slate-700 transition-colors text-xs font-medium text-slate-300"
                  >
                    <span className="flex items-center gap-1.5">
                      <FileText className="w-3 h-3 text-indigo-400" />
                      Leggi testo del contratto
                    </span>
                    {testoOpen
                      ? <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
                      : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                  </button>
                  {testoOpen && (
                    <div className="max-h-56 overflow-y-auto px-3 py-2 bg-slate-900/60 text-slate-300 text-xs leading-relaxed whitespace-pre-wrap">
                      {templateData.body_text}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Avviso dati mancanti ── */}
          {missingFields.length > 0 && (
            <div className="bg-red-900/40 border border-red-700/60 rounded-xl p-4 space-y-3">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-red-300 font-semibold text-sm">Dati obbligatori mancanti</p>
                  <p className="text-red-400 text-xs mt-0.5">
                    Completa il fascicolo prima di procedere con la firma.
                  </p>
                </div>
              </div>
              <ul className="space-y-1 pl-8">
                {missingFields.map((f, i) => (
                  <li key={i} className="text-xs text-red-300 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-red-500 shrink-0" />
                    <span className="text-red-400">{f.section}:</span>
                    <span className="font-medium">{f.field}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate(`/fascicoli-assistenziali/${fascicoloId}`)}
                className="flex items-center gap-1.5 text-xs text-red-300 hover:text-white underline underline-offset-2 pl-8 transition-colors"
              >
                <ExternalLink className="w-3 h-3" />
                Apri fascicolo e completa i dati
              </button>
            </div>
          )}

          {/* ── Area firma ── */}
          <div className={`bg-white rounded-2xl shadow-2xl overflow-hidden transition-opacity ${!canSign ? "opacity-50 pointer-events-none select-none" : ""}`}>
            {/* Header */}
            <div className="bg-indigo-600 px-6 py-4">
              <h1 className="text-white text-lg font-bold flex items-center gap-2">
                <PenLine className="w-5 h-5" />
                Firma per accettazione
              </h1>
            </div>

            <div className="p-6 space-y-5">
              {/* Luogo + Data */}
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <Label className="text-slate-700 font-medium mb-1 block">
                    Luogo <span className="text-red-500">*</span>
                  </Label>
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                    <Input
                      value={luogo}
                      onChange={(e) => setLuogo(e.target.value)}
                      placeholder="Es: Roma"
                      className="flex-1"
                      data-testid="input-luogo"
                    />
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-1.5 text-sm text-slate-500 pb-2">
                  <Calendar className="w-4 h-4" />
                  <span>{oggi}</span>
                </div>
              </div>

              {/* Canvas firma */}
              <div>
                <Label className="text-slate-700 font-medium mb-1 block">
                  Firma{" "}
                  <Badge className="bg-red-100 text-red-700 text-xs ml-1">Obbligatoria</Badge>
                </Label>
                <SignaturePadField
                  ref={sigPadRef}
                  backgroundColor="rgb(248,250,252)"
                  containerClassName="relative border-2 border-dashed border-slate-300 rounded-xl bg-slate-50 overflow-hidden"
                  canvasClassName="w-full touch-none"
                  canvasStyle={{ width: "100%", height: "220px", display: "block" }}
                  actionsClassName="flex flex-wrap items-center justify-between gap-2 mt-2"
                />
              </div>

              {/* Pulsante firma */}
              <Button
                onClick={handleSign}
                disabled={saving || !canSign}
                className="w-full h-12 text-base bg-indigo-600 hover:bg-indigo-700 shadow-lg disabled:opacity-60"
                data-testid="btn-firma-contratto"
              >
                {saving ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generazione contratto...</>
                ) : (
                  <><Check className="w-5 h-5 mr-2" />Firma e genera contratto</>
                )}
              </Button>

              <p className="text-center text-xs text-slate-400">
                Firmando si accettano i termini del contratto di ospitalità.
                Il documento firmato verrà salvato automaticamente.
              </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex gap-2 text-xs">
      <span className="text-slate-400 shrink-0 w-16">{label}:</span>
      <span className="text-slate-200 font-medium">{value || "N/D"}</span>
    </div>
  );
}
